import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Plus, Trash2 } from 'lucide-react';

const BentoEditor = () => {
    const [title, setTitle] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const { data } = await api.get('/sections/Bento');
                if (data && data.content) {
                    const contentMap = {};
                    data.content.forEach(item => contentMap[item.key] = item.value);

                    if (contentMap.title) setTitle(contentMap.title);
                    if (contentMap.items) {
                        try {
                            setItems(JSON.parse(contentMap.items));
                        } catch (e) {
                            console.error('Error parsing items', e);
                        }
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    const handleAddItem = () => {
        setItems([...items, { id: Date.now(), title: '', description: '', colSpan: 1 }]);
    };

    const handleRemoveItem = (id) => {
        setItems(items.filter(i => i.id !== id));
    };

    const handleItemChange = (id, field, value) => {
        setItems(items.map(i => i.id === id ? { ...i, [field]: value } : i));
    };

    const handleImageUpload = async (id, file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            handleItemChange(id, 'image', data.path);
        } catch (error) {
            console.error("Upload failed", error);
            setMessage("Image upload failed");
        }
    };

    const handleSave = async () => {
        try {
            const contentArray = [
                { key: 'title', type: 'text', value: title },
                { key: 'items', type: 'json', value: JSON.stringify(items) }
            ];
            await api.put('/sections/Bento', { content: contentArray });
            setMessage('Bento Grid updated successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Error saving bento grid');
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="max-w-4xl mx-auto bg-white p-8 rounded shadow text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Edit Bento Grid Section</h2>
            {message && <div className={`p-4 mb-4 rounded ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{message}</div>}

            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Section Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full mt-1 p-2 border rounded"
                    />
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">Grid Items</label>
                        <button onClick={handleAddItem} type="button" className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                            <Plus className="w-4 h-4 mr-1" /> Add Card
                        </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {items.map((item) => (
                            <div key={item.id} className="border p-4 rounded bg-gray-50 relative group">
                                <button
                                    onClick={() => handleRemoveItem(item.id)}
                                    className="absolute top-2 right-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-xs text-gray-500">Title</label>
                                        <input
                                            type="text"
                                            value={item.title}
                                            onChange={(e) => handleItemChange(item.id, 'title', e.target.value)}
                                            className="w-full mt-1 p-2 border rounded text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500">Column Span (1-3)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="3"
                                            value={item.colSpan || 1}
                                            onChange={(e) => handleItemChange(item.id, 'colSpan', parseInt(e.target.value))}
                                            className="w-full mt-1 p-2 border rounded text-sm"
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-xs text-gray-500">Description</label>
                                        <textarea
                                            value={item.description}
                                            onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                                            className="w-full mt-1 p-2 border rounded text-sm h-16"
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-xs text-gray-500">Image</label>
                                        {item.image && (
                                            <div className="text-xs text-green-600 truncate mb-1">
                                                path: {item.image}
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageUpload(item.id, e.target.files[0])}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <button onClick={handleSave} className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800">
                    Save Changes
                </button>
            </div>
        </div>
    );
};

export default BentoEditor;
